import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Skeleton,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import _ from 'lodash';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import {
  date as commonDate,
  finleyAssert,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  FundingRequestEntitiesFragmentGQLType,
  FundingLenderSharesFragmentGQLType,
  ResolvedLoanFragmentGQLType,
  useGetCreditFacilityMetricsQuery,
} from 'types/dashboard-core-gqlTypes.generated.d';

import { LoanPricingOption } from './LoanPricingOption';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import { Typography } from '~/packages/common-frontend/components/Typography';
import {
  formatCurrencyAmount,
  percentageToPercentageLabel,
  truncateUuid,
} from '~/packages/common-frontend/utils';
import { Bank } from '~/packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { FundingRequestStatusBadge } from '~/packages/funding-requests/ui/shared/StatusBadge';
import { DownloadAllLiveFundingNoticesButton } from '~/packages/funding-requests/ui/submit/DownloadAllLiveFundingNoticesButton';
import {
  MarkLenderShareComponentFundedButton,
  MarkLenderShareComponentFundedMenuItem,
} from '~/packages/loans/ui/components/MarkLenderShareComponentFundedButton';
import { UndoLenderShareComponentFundedMenuItem } from '~/packages/loans/ui/components/MarkLenderShareComponentFundedButton/UndoLenderShareComponentFunded';
import { DownloadLiveFundingNoticeButton } from '~/packages/loans/ui/DownloadLiveFundingNoticeButton';

// constants
const TABLE_CELL_STYLES = {
  color: colors.gray[500],
  minWidth: '5rem',
  whiteSpace: 'normal',
};

const TABLE_HEADER_STYLES = {
  color: colors.black[95],
  fontSize: '14px',
  fontWeight: 500,
};

const FundingRequestTag = ({
  label,
  icon,
  tooltip,
}: {
  label: string;
  icon: string;
  tooltip: string;
}) => (
  <Tooltip label={tooltip} placement="top">
    <Tag
      borderRadius="64px"
      p="8px 16px"
      borderColor={colors.gray[100]}
      borderWidth="1px"
      backgroundColor="white"
      cursor="default"
    >
      <Icon icon={icon} />
      <Text ml="8px" fontSize="14px" fontWeight={500}>
        {label}
      </Text>
    </Tag>
  </Tooltip>
);

const FundingRequestTable = ({
  fundingRequest,
  lenderShares,
  loan,
}: {
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  lenderShares: FundingLenderSharesFragmentGQLType[];
  loan: ResolvedLoanFragmentGQLType;
}) => {
  return (
    <Flex overflowX="auto" overflowY="auto" w="100%">
      <Table>
        <Thead>
          <Tr>
            <Th sx={TABLE_HEADER_STYLES}>Funded</Th>
            <Th sx={TABLE_HEADER_STYLES}>Lender</Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Lender Share
            </Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Lender Funding Share
            </Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Lender All-In Rate
            </Th>
            <Th sx={TABLE_HEADER_STYLES} textAlign="right">
              Projected Interest
            </Th>
            <Th sx={TABLE_HEADER_STYLES}>Lender Funded Date</Th>
            {/* //TODO add menu styles here @eldarshamukhamedov @markhaghani */}
            <Th
              fontWeight={500}
              fontSize="14px"
              color="black"
              width="5%"
              textAlign="center"
            >
              <Menu>
                <MenuButton
                  fontSize="14px"
                  fontWeight="500"
                  aria-label="Download and email all funding requests"
                >
                  <HStack>
                    <span>Notice</span>
                    <Icon icon="chevron-down" />
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem as="div">
                    <DownloadAllLiveFundingNoticesButton
                      loanId={loan.id}
                      creditFacilityId={loan.facility.creditFacilityId}
                      fundingRequestId={fundingRequest.fundingRequestId}
                    />
                  </MenuItem>
                </MenuList>
              </Menu>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {fundingRequest.fundingComponents.map((fundingComponent, idx) => {
            const lenderShare = lenderShares.find(
              (ls) => ls.lenderShareId === fundingComponent.lenderShareId,
            );
            finleyAssert(
              lenderShare,
              'Lender share should always exist for a loan funding components',
            );

            const border =
              idx === fundingRequest.fundingComponents.length - 1
                ? 'none'
                : undefined;
            return (
              <Tr key={fundingComponent.lenderShareId}>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'center',
                  }}
                >
                  {(() => {
                    // Can only mark as funded if loan is in "initiated" or "funded" or "overdue" state.
                    // For "initiated" (per product), the admin agent may collect money from
                    // their participants prior to borrower actually receiving the ACH transfer
                    if (
                      loan.calculatedLoanStatus !== 'initiated' &&
                      loan.calculatedLoanStatus !== 'funded' &&
                      loan.calculatedLoanStatus !== 'overdue'
                    ) {
                      return <Typography>--</Typography>;
                    }

                    if (fundingComponent.fundTransferDate) {
                      if (!commonUtils.flags.isAdminAgentFrontingEnabled) {
                        return <Icon icon="check" color="green" />;
                      }

                      return (
                        <Menu>
                          <MenuButton fontSize="14px" fontWeight="500">
                            <HStack>
                              <Typography
                                fontWeight="semibold"
                                colorScheme="v2-aurora"
                              >
                                Funded
                              </Typography>
                              <Icon icon="chevron-down" />
                            </HStack>
                          </MenuButton>
                          <MenuList>
                            <MarkLenderShareComponentFundedMenuItem
                              fundingRequest={fundingRequest}
                              fundingComponent={fundingComponent}
                            />
                            <UndoLenderShareComponentFundedMenuItem
                              fundingComponent={fundingComponent}
                            />
                          </MenuList>
                        </Menu>
                      );
                    }

                    return (
                      <MarkLenderShareComponentFundedButton
                        fundingRequest={fundingRequest}
                        fundingComponent={fundingComponent}
                      />
                    );
                  })()}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    fontWeight: 500,
                    border,
                    color: 'black',
                  }}
                >
                  {lenderShare.lenderOrganization.name}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {percentageToPercentageLabel(
                    lenderShare.allocationPercent * 100,
                    6,
                    true,
                  )}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrencyAmount(
                    fundingComponent.requestedAmount,
                    Bank.Currency.USD,
                  )}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {/* // TODO: @markhaghani all in rate should be component level as. ex: admin agent can have different rate */}
                  {percentageToPercentageLabel(
                    loan.pricing.current.allInRate * 100,
                    5,
                    true,
                  )}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    border,
                    textAlign: 'right',
                  }}
                >
                  {formatCurrencyAmount(
                    fundingComponent.projectedInterest,
                    Bank.Currency.USD,
                  )}
                </Td>
                <Td sx={{ ...TABLE_CELL_STYLES, border }}>
                  {fundingComponent.fundTransferDate
                    ? commonDate.fullDateStrToHumanDateString(
                        fundingComponent.fundTransferDate,
                      )
                    : '-'}
                </Td>
                <Td
                  sx={{
                    ...TABLE_CELL_STYLES,
                    textAlign: 'center',
                    border,
                  }}
                >
                  <DownloadLiveFundingNoticeButton
                    lenderShareId={lenderShare.lenderShareId}
                    fundingRequestComponentId={
                      fundingComponent.fundingRequestComponentId
                    }
                  />
                  {/* // TODO: @markhaghani, add email funding request v2 launch */}
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </Flex>
  );
};

export const FundingRequestDetails = ({
  loan,
  fundingRequest,
}: {
  loan: ResolvedLoanFragmentGQLType;
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
}) => {
  return (
    <VStack w="100%" gap="1rem">
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Funding Request ID</Typography>
        <Typography variant="base-muted">
          {truncateUuid(fundingRequest.fundingRequestId)}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Activity Type</Typography>
        <Typography variant="base-muted">
          {_.startCase(loan.loanType)}
        </Typography>
      </HStack>
      <LoanPricingOption loan={loan} loading={false} />
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Draw Request Amount</Typography>
        <Typography variant="base-muted">
          {formatCurrencyAmount(
            fundingRequest.totalRequestedAmount,
            Bank.Currency.USD,
          )}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Funding Date</Typography>
        <Typography variant="base-muted">
          {commonDate.fullDateStrToHumanDateString(
            fundingRequest.originationDate,
          )}
        </Typography>
      </HStack>
      <HStack justifyContent="space-between" w="100%">
        <Typography fontWeight="medium">Maturity Date</Typography>
        <Typography variant="base-muted">
          {commonDate.fullDateStrToHumanDateString(loan.dates.maturityDate)}
        </Typography>
      </HStack>
    </VStack>
  );
};

export const FundingRequestDrawer = ({
  isOpen,
  fundingRequestId,
  loan,
  onToggle,
}: {
  isOpen: boolean;
  fundingRequestId: string;
  loan: ResolvedLoanFragmentGQLType;
  onToggle: (isOpen: boolean) => void;
}) => {
  const { data: metricsDataRequest } = useGetCreditFacilityMetricsQuery({
    skip: !loan?.facility,
    variables: {
      creditFacilityId: loan?.facility.creditFacilityId as string,
      postingDate: commonDate.currentDateStr(),
    },
    fetchPolicy: 'no-cache',
  });

  const metrics = metricsDataRequest?.metrics;

  const fundingRequest = loan.funding.requests.find(
    (fr) => fr.fundingRequestId === fundingRequestId,
  );

  finleyAssert(
    fundingRequest,
    `Funding Request must exist [fundingRequestId: ${fundingRequestId}] [loanId: ${loan.id}]`,
  );

  return (
    <Drawer
      isOpen={isOpen}
      placement="right"
      onClose={() => onToggle(false)}
      size="xl"
    >
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader mt="1.5rem" px="3rem" pb="0">
          <Typography variant="section-title" trim>
            Funding Request {truncateUuid(fundingRequest.fundingRequestId)}
          </Typography>
        </DrawerHeader>
        <Divider my="1.5rem" w="100%" />
        <DrawerBody px="3rem">
          <VStack align="flex-start" gap="2rem">
            <Flex>
              <FundingRequestStatusBadge
                status={fundingRequest.fundingRequestStatus}
              />
            </Flex>
            <Divider />
            <Typography variant="section-subtitle" trim>
              Funding Request
            </Typography>
            <FundingRequestDetails
              fundingRequest={fundingRequest}
              loan={loan}
            />

            <Divider />
            <Typography variant="section-subtitle" trim>
              Facility Details
            </Typography>
            <VStack w="100%" gap="1rem">
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Name</Typography>
                <Typography variant="base-muted">
                  {_.startCase(loan.facility.creditFacilityName)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Borrower</Typography>
                <Typography variant="base-muted">
                  {_.startCase(loan.parties.borrowerOrganization.name)}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">Global Commitment</Typography>
                <Typography variant="base-muted">
                  {formatCurrencyAmount(
                    loan.facility.commitmentAmount,
                    Bank.Currency.USD,
                  )}
                </Typography>
              </HStack>
              <HStack justifyContent="space-between" w="100%">
                <Typography fontWeight="medium">
                  Global Outstanding Balance
                </Typography>
                {metrics ? (
                  <Typography variant="base-muted">
                    {formatCurrencyAmount(
                      metrics.outstandingPrincipalBalance,
                      Bank.Currency.USD,
                    )}
                  </Typography>
                ) : (
                  <Skeleton w="10rem" h="1rem" />
                )}
              </HStack>
            </VStack>

            <Divider />
            <Flex justify="flex-end" w="100%">
              <Button
                as={RouterLink}
                to={`/funding-requests/${fundingRequest.fundingRequestId}`}
                variant="v2-ghost"
                rightIcon={<Icon icon="chevron-right" />}
              >
                View Funding Request
              </Button>
            </Flex>
          </VStack>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export const FundingRequestSnapshotContainer = ({
  loan,
  fundingRequest,
  lenderShares,
  loading,
}: {
  loan: ResolvedLoanFragmentGQLType;
  fundingRequest: FundingRequestEntitiesFragmentGQLType;
  lenderShares: FundingLenderSharesFragmentGQLType[];
  loading: boolean;
}) => {
  const { isOpen, onToggle } = useDisclosure();

  if (loading || !loan) return <Skeleton height="200px" w="100%" />;

  return (
    <>
      <Card
        size="lg"
        variant="outline"
        w="100%"
        p="1rem"
        id="funding-request-snapshot-container"
      >
        <CardHeader
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            fontWeight: 500,
            gap: '1rem',
            justifyContent: 'space-between',
            w: '100%',
          }}
        >
          <Flex gap="1rem" align="center">
            <Typography variant="section-subtitle" trim>
              Funding Request
            </Typography>
            <Button variant="link" onClick={onToggle} colorScheme="blue">
              <Typography
                colorScheme="blue"
                fontWeight="medium"
                variant="base-muted"
              >
                {truncateUuid(fundingRequest.fundingRequestId)}
              </Typography>
            </Button>
            <FundingRequestStatusBadge
              status={fundingRequest.fundingRequestStatus}
            />
          </Flex>
          <HStack gap="1rem">
            <FundingRequestTag
              label={commonDate.fullDateStrToHumanDateString(
                fundingRequest.originationDate,
              )}
              icon="calendar"
              tooltip="Funding Date"
            />
            <FundingRequestTag
              icon="money-bill-transfer"
              label={commonUtils.toTitleCase(fundingRequest.fundingRequestType)}
              tooltip="Funding Request Type"
            />
            <FundingRequestTag
              label={
                formatCurrencyAmount(
                  fundingRequest.totalRequestedAmount,
                  Bank.Currency.USD,
                ) ?? '-'
              }
              icon="circle-dollar"
              tooltip="Funded Amount"
            />
          </HStack>
        </CardHeader>
        <CardBody pt="1rem">
          <FundingRequestTable
            fundingRequest={fundingRequest}
            lenderShares={lenderShares}
            loan={loan}
          />
        </CardBody>
      </Card>
      <FundingRequestDrawer
        fundingRequestId={fundingRequest.fundingRequestId}
        loan={loan}
        isOpen={isOpen}
        onToggle={onToggle}
      />
    </>
  );
};
